import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem/GridItem";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import TextField from "@insite/mobius/TextField/TextField";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import {
    CallbackType,
    ErrorObjectType,
    InputObjectType,
    validate,
    ValidatorRuleType,
} from "@tCentro/Common/Utilities/validate";
import downloadLineCard from "@tCentro/Handlers/CENDownLoadLineCard";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    downloadLineCard,
};

interface OfferingsModel {
    fields: {
        label: string;
    };
}
const enum fields {
    textField = "textField",
    description = "description",
    offerings = "offerings",
    emailTo = "emailTo",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.textField]: string;
        [fields.description]: string;
        [fields.offerings]: OfferingsModel[];
        [fields.emailTo]: string;
    };
    extendedStyles?: LineCardStyles;
}

export interface LineCardStyles {
    nameGridItem?: GridItemProps;
    submitButton?: InjectableCss;
    buttonWrapper?: InjectableCss;
    descriptionStyle?: InjectableCss;
    checkboxStyle?: InjectableCss;
}

export const lineCardStyles: LineCardStyles = {
    nameGridItem: {
        width: 12,
        css: css`
            margin-bottom: 14px !important;
            margin-top: 20px;

            label {
                color: #000000;
                font-weight: 400;
            }
        `,
    },

    submitButton: {
        css: css`
            background: #276b40;
            color: #ffffff;
            padding: 16px 21px;
            font-size: 14px;
            font-weight: 500;
            width: 100%;
            height: 100%;
            border-radius: 40px;
            border: none;
            font-family: "Poppins";
            &:hover {
                background: #276b40;
            }
        `,
    },

    buttonWrapper: {
        css: css`
            margin-top: 32px;
        `,
    },

    descriptionStyle: {
        css: css`
            font-size: 16px;
            padding: 0px;
            font-weight: 500;
            font-family: "Poppins";
            color: black;
            margin-top: 5px;
        `,
    },

    checkboxStyle: {
        css: css`
            font-size: 16px;
            font-family: "Poppins";
            color: black;
            margin-bottom: 4px;
        `,
    },
};

const styles = lineCardStyles;

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export const intialErrorFormState = {
    name: "",
    email: "",
    companyName: "",
};

export interface ValidatorConfigType {
    [key: string]: ValidatorRuleType;
}

export interface FormErrorsStateType {
    [key: string]: string | undefined;
}

export const downloadFormValidatorConfig: ValidatorConfigType = {
    name: {
        required: true,
        label: "Name",
        regexMsg: "Name is required.",
    },
    companyName: {
        required: true,
        label: "Company Name",
        regexMsg: "Company Name is required.",
    },
    email: {
        required: true,
        regex: /\w+([-+."]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/,
        label: "Email",
        regexMsg: "Please enter valid email.",
    },
};

export const CENLineCardOldForm: FC<Props> = ({ fields, downloadLineCard }) => {
    const history = useHistory();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [selectedOfferings, setSelectedOfferings] = useState<Record<string, string>>({});

    const [formErrors, setFormErrors] = React.useState<FormErrorsStateType>(intialErrorFormState);
    const [isButtonDisabled, setIsButtonDisabled] = React.useState(true);

    const validateSubmitDisabled = (errorsState: FormErrorsStateType) => {
        return Object.keys(errorsState).length !== 0;
    };

    const validateHandler = (
        input: HTMLInputElement | HTMLSelectElement | any,
        customValidation?: CallbackType,
    ): void => {
        const inputObject: InputObjectType = {
            name: input.name,
            value: input.value,
        };
        const validatorConfig = downloadFormValidatorConfig[input.name as keyof ValidatorConfigType];
        const isValidate: ErrorObjectType | undefined = validate(inputObject, validatorConfig, customValidation);
        const formErrs = { ...formErrors };
        if (isValidate === undefined) {
            delete formErrs[input.name as keyof FormErrorsStateType];
            setFormErrors(formErrs);
        } else {
            formErrs[input.name] = isValidate ? isValidate.errorMsg : isValidate;
            setFormErrors(formErrs);
        }
        if (!validatorConfig.required && input.value === "") {
            delete formErrs[input.name as keyof FormErrorsStateType];
            setFormErrors(formErrs);
        }

        setIsButtonDisabled(validateSubmitDisabled(formErrs));
    };

    const nameChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setName(input.value);
    };

    const emailChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setEmail(input.value);
    };

    const companyNameChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setCompanyName(input.value);
    };

    const handleSelectOffering = (event: React.SyntheticEvent, value: boolean, label: string) => {
        if (value) {
            setSelectedOfferings({ ...selectedOfferings, [label]: label });
        } else {
            const offrs = { ...selectedOfferings };
            delete offrs[label];
            setSelectedOfferings(offrs);
        }
    };

    const submitHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
        setIsButtonDisabled(true);
        const formHasErrors = validateSubmitDisabled(formErrors);
        if (formHasErrors) {
            return false;
        }
        function getDataLayer() {
            return (window as any).dataLayer;
        }

        const dataLayer = getDataLayer();
        if (dataLayer) {
            dataLayer.push({
                event: `CenDownloadLineCardForm`,
            });
        }
        const businessDivisions = Object.keys(selectedOfferings);
        downloadLineCard({
            name,
            email,
            companyName,
            emailTo: fields.emailTo,
            businessDivisions,
            uri: "",
            properties: {},
            onSuccess: () => {
                history.push("/LineCard");
            },
        });
    };

    return (
        <>
            <GridItem {...styles.nameGridItem}>
                <TextField
                    label={translate("Name")}
                    name="name"
                    value={name}
                    onChange={e => nameChangeHandler(e.currentTarget)}
                    onBlur={e => validateHandler(e.currentTarget)}
                    error={!!formErrors.name && formErrors.name}
                    iconProps={{
                        color: "danger",
                        src: formErrors.name ? AlertTriangle : undefined,
                    }}
                    required
                />
            </GridItem>
            <GridItem {...styles.nameGridItem}>
                <TextField
                    label={translate("Email")}
                    name="email"
                    value={email}
                    onChange={e => emailChangeHandler(e.currentTarget)}
                    onBlur={e => validateHandler(e.currentTarget)}
                    error={!!formErrors.email && formErrors.email}
                    iconProps={{
                        color: "danger",
                        src: formErrors.email ? AlertTriangle : undefined,
                    }}
                    required
                />
            </GridItem>
            <GridItem {...styles.nameGridItem}>
                <TextField
                    label={translate("Company Name")}
                    name="companyName"
                    value={companyName}
                    onChange={e => companyNameChangeHandler(e.currentTarget)}
                    onBlur={e => validateHandler(e.currentTarget)}
                    error={!!formErrors.companyName && formErrors.companyName}
                    iconProps={{
                        color: "danger",
                        src: formErrors.companyName ? AlertTriangle : undefined,
                    }}
                    required
                />
            </GridItem>

            {fields.offerings && fields.offerings.length > 0 ? (
                <CheckboxGroup label={translate(fields.description)}>
                    {fields.offerings.map((offering, index) => (
                        <StyledWrapper {...styles.checkboxStyle} key={offering.fields.label}>
                            <Checkbox
                                checked={selectedOfferings[offering.fields.label] !== undefined}
                                onChange={(e, val) => handleSelectOffering(e, val, offering.fields.label)}
                            >
                                {offering.fields.label}
                            </Checkbox>
                        </StyledWrapper>
                    ))}
                </CheckboxGroup>
            ) : null}

            <StyledWrapper {...styles.buttonWrapper}>
                <Button onClick={submitHandler} disabled={isButtonDisabled} {...styles.submitButton}>
                    {translate("SUBMIT")}
                </Button>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(CENLineCardOldForm),
    definition: {
        group: "Common",
        displayName: "Line Card Request Old Form",
        fieldDefinitions: [
            {
                name: fields.description,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "Which business division best describes your company?",
                displayName: "Offerings List Title",
            },
            {
                name: fields.offerings,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.label;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "label",
                        displayName: "Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
            },
            {
                name: fields.emailTo,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                displayName: "Receiver Email Id",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
